/**
 * Family Orders — the PARENT weekly ordering surface for /orders/family.
 *
 * The parent analog of `SchoolOrdersView`: it resolves the household's
 * children, groups them by campus for data purposes, and hands off to
 * `FamilyWeekOrdersView` — the consolidated weekly grid where EVERY child
 * appears in every day card regardless of campus (each child keeps their own
 * campus's menu + deadline). There is deliberately NO location switcher — a
 * parent thinks in kids, not campuses. The week stepper lives in the page
 * header; the selected week's state is owned here.
 *
 * Rendered full-bleed (like the school + kitchen surfaces) so the header stays
 * fixed while the day-card list scrolls. Checkout reuses the shared
 * `createCart → addCartItem → submitCart` pipeline, submitting one
 * `FAMILY_DIRECT` OrderBatch per (serviceDate, mealtime) group.
 */

import { Button } from '@ordo/ui/button'
import { cn } from '@ordo/ui/cn'
import { EmptyState } from '@ordo/ui/empty-state'
import type { SearchableSelectOption } from '@ordo/ui/searchable-select'
import { Skeleton } from '@ordo/ui/skeleton'
import { Calendar, Plus, Printer, School, Users } from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { useBillingFamilyQuery } from '@/api/billing-family'
import {
  listStudentsNeedingRequiredInfo,
  type MyFamilyResponse,
  selectDisputeBlock,
  selectPaymentFailure,
  useEnableSelfOrderingMutation,
  useTagsByKindQuery,
} from '@/api/family'
import { useFamilyOrdersQuery } from '@/api/family-orders'
import { listOrderingStudents, needsSelfDinerProvisioning, useMyOrderingFamilyQuery } from '@/api/orders-family'
import { isChannelEnabled, useOrgOrderingConfigQuery } from '@/api/org-ordering-config'
import { ParentOrderingDisabledBanner } from '@/components/banners'
import { AutoReloadAlert } from '@/components/billing/auto-reload-alert'
import { AddMemberSheet } from '@/components/family/add-member-sheet'
import { CompleteStudentInfoSheet } from '@/components/family/complete-student-info-sheet'
import { DisputeBlockBanner } from '@/components/family/dispute-block-banner'
import { MissingInfoBanner } from '@/components/family/missing-info-banner'
import { PaymentFailureBanner } from '@/components/family/payment-failure-banner'
import { FamilyPrintDialog } from '@/components/orders/family/print/family-print-dialog'
import {
  acceptsParentOrders,
  type FamilyOrderLocation,
  groupStudentsByLocation,
} from '@/components/orders/family/week/columns'
import { buildMergedDayBadges, nextOpenWeekMondayAll } from '@/components/orders/family/week/day-badges'
import { useFamilyWeekBanner } from '@/components/orders/family/week/family-week-banner'
import { FamilyWeekOrdersView } from '@/components/orders/family/week/week-orders-view'
import { WeekNav } from '@/components/orders/school/week-nav'
import { OrdoPageHeader } from '@/components/ordo-page-header'
import { buildFamilyAlreadyOrdered } from '@/lib/family-orders/already-ordered'
import { mondayOf, todayKey, weekOf } from '@/lib/school-orders/calendar'
import { NO_SCHOOLS_EMPTY, SupportEmailButton } from '@/lib/support'
import { useFamilyCartSync } from '@/stores/family-cart'
import { useProfile } from '@/stores/profile'

function Shell({
  switcher,
  controls,
  scrolled,
  children,
}: {
  switcher?: ReactNode
  controls?: ReactNode
  /** Day list is scrolled away from the top — compact the mobile controls row. */
  scrolled?: boolean
  children: ReactNode
}) {
  return (
    <div className="mx-auto flex h-full w-full min-h-0 max-w-[100rem] flex-col px-4">
      <div className="md:pt-4 lg:pt-5">
        <OrdoPageHeader icon={Calendar} title="Orders" className="mb-0" />
        {switcher}
      </div>
      {controls ? (
        <div
          className={cn(
            '-mx-4 flex items-center justify-between gap-3 border-b px-4 transition-[padding,border-color] duration-200 md:mx-0 md:border-0 md:px-0 md:py-0 md:pt-3 lg:pt-4',
            scrolled ? 'border-border py-2' : 'border-transparent py-5',
          )}>
          {controls}
        </div>
      ) : null}
      {children}
    </div>
  )
}

export function FamilyWeekOrdersPage({ switcher }: { switcher?: ReactNode }) {
  // Hydrate the server-synced cart (and keep it fresh on focus) so the
  // right-rail shows the account's cart from any device.
  useFamilyCartSync()
  const familyQuery = useMyOrderingFamilyQuery()
  const students = useMemo(() => listOrderingStudents(familyQuery.data), [familyQuery.data])
  const locations = useMemo(() => groupStudentsByLocation(students), [students])

  // Staff (teachers) order for themselves: auto-provision them as their own
  // diner (idempotent) so they can order without being told to "add a student"
  // — they're not a parent. The trigger is "the teacher isn't in the diner list
  // yet", NOT "the list is empty": a teacher who is ALSO a parent already has
  // children here, and gating on emptiness meant self-ordering was unreachable
  // for them by any path, in the UI or via Ops (PROD-1163). Guarded by a ref so
  // it fires once per mount.
  const { profile, loading: profileLoading } = useProfile()
  const isStaff = (profile?.profileCodes ?? []).includes('TEACHER')
  const needsSelfDiner = needsSelfDinerProvisioning(students, isStaff)
  const enableSelfOrdering = useEnableSelfOrderingMutation()
  const selfProvisionedRef = useRef(false)
  useEffect(() => {
    if (selfProvisionedRef.current) {
      return
    }
    if (familyQuery.isPending || familyQuery.error || !profile) {
      return
    }
    if (!needsSelfDiner) {
      return
    }
    selfProvisionedRef.current = true
    enableSelfOrdering.mutate()
  }, [familyQuery.isPending, familyQuery.error, profile, needsSelfDiner, enableSelfOrdering])

  // "Add a student" entry for staff — since the "My Family" nav is hidden until
  // they hold a PARENT profile, this is how a teacher adds their first child
  // (which upgrades them to Teacher + Parent).
  const [addOpen, setAddOpen] = useState(false)
  // Print a child's week or month of orders (PROD-1064).
  const [printOpen, setPrintOpen] = useState(false)
  const allergenQuery = useTagsByKindQuery('ALLERGEN')
  const dietaryQuery = useTagsByKindQuery('DIETARY')
  const allergenOptions = useMemo<SearchableSelectOption[]>(
    () => (allergenQuery.data?.tags ?? []).map((t) => ({ value: t.id, label: t.name })),
    [allergenQuery.data],
  )
  const dietaryOptions = useMemo<SearchableSelectOption[]>(
    () => (dietaryQuery.data?.tags ?? []).map((t) => ({ value: t.id, label: t.name })),
    [dietaryQuery.data],
  )

  // Order-blocking student-setup gaps (missing REQUIRED facts + campus
  // confirmation) — drives the banner over the week grid and the
  // Finish-setup sheet, mirroring /family.
  const studentsNeedingInfo = useMemo(() => listStudentsNeedingRequiredInfo(familyQuery.data), [familyQuery.data])
  const [completeSheetOpen, setCompleteSheetOpen] = useState(false)
  // Which banner owns the slot over the week grid. Setup outranks BOTH the
  // auto-reload alert and the auto-order nudge, so the parent sees one CTA.
  const { slot: bannerSlot } = useFamilyWeekBanner()

  // Compact the mobile controls row (week stepper) once the day list scrolls.
  const [listScrolled, setListScrolled] = useState(false)

  // Selected week — owned here so the stepper can sit in the header. Once the
  // campuses (and their deadline policies) load, jump to the next week whose
  // order window is still open somewhere, so a parent doesn't land on an
  // already-closed week.
  const [anchorKey, setAnchorKey] = useState(() => mondayOf(todayKey()))
  const didDefaultWeek = useRef(false)
  useEffect(() => {
    if (didDefaultWeek.current || locations.length === 0) {
      return
    }
    didDefaultWeek.current = true
    setAnchorKey((prev) => nextOpenWeekMondayAll(locations, prev))
  }, [locations])
  const selectWeek = (mondayKey: string) => setAnchorKey(mondayOf(mondayKey))
  const weekKeys = useMemo(() => weekOf(anchorKey), [anchorKey])

  const familyOrdersQuery = useFamilyOrdersQuery({
    weekStart: weekKeys[0] ?? anchorKey,
    enabled: locations.length > 0,
  })
  const orders = useMemo(() => familyOrdersQuery.data?.orders ?? [], [familyOrdersQuery.data])
  const alreadyOrdered = useMemo(() => buildFamilyAlreadyOrdered(orders), [orders])
  const orderedDates = useMemo(() => new Set(orders.map((o) => o.serviceDate.slice(0, 10))), [orders])
  const dayBadges = useMemo(() => buildMergedDayBadges(locations, weekKeys, orders), [locations, weekKeys, orders])

  // "Review & Checkout" opens the cart/review page rather than submitting
  // directly — placing the order is a deliberate step there.
  const navigate = useNavigate()

  // The first-visit menu-intro teleport (PROD-12: auto-open the next open
  // day's menu for never-ordered parents) was REMOVED 2026-07-29 (Sheila):
  // landing always stays on the week view — a day opens only when the user
  // taps it.
  const handleCheckout = (): void => {
    void navigate('/orders/family/checkout')
  }

  let content: ReactNode
  if (familyQuery.isPending && !familyQuery.data) {
    content = <ViewLoading />
  } else if (familyQuery.error) {
    content = <ErrorPanel message={familyQuery.error.message} />
  } else if (students.length === 0) {
    if (isStaff || profileLoading) {
      // A teacher with no diners is auto-provisioned as their own diner; hold
      // on a loading state while that runs (or surface an error if it failed).
      content = enableSelfOrdering.isError ? (
        <ErrorPanel message="We couldn't set up ordering for you. Please refresh and try again." />
      ) : (
        <ViewLoading />
      )
    } else {
      content = <NoStudentsState />
    }
  } else if (locations.length > 0) {
    content = (
      <div className="flex min-h-0 flex-1 flex-col gap-3 md:pt-3">
        {locations.some(acceptsParentOrders) ? <AutoReloadGate /> : null}
        {/* EVERY order-blocking student-setup gap surfaces here, on the page
            parents actually land on — missing REQUIRED facts (grade, …) AND
            campus confirmation — with the same copy + Finish-setup sheet as
            /family. A campus-less child is dropped from the day cards by the
            location grouping, so without this banner they'd silently vanish;
            a grade-less child renders but can't be ordered for (PROD-385). */}
        {locations.some(acceptsParentOrders) && bannerSlot === 'missing-info' ? (
          <MissingInfoBanner students={studentsNeedingInfo} onFinishSetup={() => setCompleteSheetOpen(true)} />
        ) : null}
        {locations.some(acceptsParentOrders) ? (
          <PaymentFailureBanner paymentFailure={selectPaymentFailure(familyQuery.data)} />
        ) : null}
        {locations.some(acceptsParentOrders) ? (
          <DisputeBlockBanner disputeBlock={selectDisputeBlock(familyQuery.data)} />
        ) : null}
        {locations.map((l) => (
          <CampusParentOrderingGate key={l.organizationId + l.id} location={l} />
        ))}
        <FamilyWeekOrdersView
          locations={locations}
          anchorKey={anchorKey}
          onAnchorChange={selectWeek}
          alreadyOrdered={alreadyOrdered}
          dayBadges={dayBadges}
          orderedDates={orderedDates}
          onCheckout={handleCheckout}
          onScrolledChange={setListScrolled}
        />
      </div>
    )
  } else {
    // Students exist but none has an active membership at a school campus —
    // without an explicit state here the page body rendered literally
    // nothing (PROD-385).
    content = <CampusNeededState family={familyQuery.data ?? null} onFinishSetup={() => setCompleteSheetOpen(true)} />
  }

  const controls =
    locations.length > 0 ? (
      <>
        <WeekNav anchorKey={anchorKey} onAnchorChange={selectWeek} />
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPrintOpen(true)}>
            <Printer className="mr-1.5 h-4 w-4" aria-hidden />
            Print
          </Button>
          {isStaff ? (
            <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" aria-hidden />
              Add a student
            </Button>
          ) : null}
        </div>
      </>
    ) : null

  return (
    <>
      <Shell switcher={switcher} controls={controls} scrolled={listScrolled}>
        {content}
      </Shell>
      <FamilyPrintDialog open={printOpen} onOpenChange={setPrintOpen} students={students} weekAnchorKey={anchorKey} />
      <AddMemberSheet
        open={addOpen}
        onOpenChange={setAddOpen}
        initialMode="student-method"
        allergenOptions={allergenOptions}
        dietaryOptions={dietaryOptions}
        onAdded={() => {
          void familyQuery.refetch()
        }}
      />
      <CompleteStudentInfoSheet
        open={completeSheetOpen}
        onOpenChange={setCompleteSheetOpen}
        students={studentsNeedingInfo}
        onDone={() => {
          void familyQuery.refetch()
        }}
      />
    </>
  )
}

/**
 * Auto-reload gate: the household is balance-funded, auto-reload can't fire,
 * and live auto-orders are charged to that balance — so meals the parent
 * thinks are handled are being skipped (or have already been auto-paused by
 * the subscription runner).
 *
 * Before this, the only place that said so was the auto-order's own detail
 * page, two taps away. The server derives the whole condition
 * (`GET /api/family-subscriptions` → `autoReloadAlerts`); this renders it.
 * Nothing shows while the query is loading, on an INVOICED household, or once
 * auto-reload is armed.
 */
function AutoReloadGate() {
  // One derivation owns the banner slot above the day cards — `AutoOrderNudge`
  // asks the same hook and stands down when this wins, so the funding alert and
  // the "auto-order is on" promo can never contradict each other on screen.
  const { slot, alert } = useFamilyWeekBanner()
  // Only fetched to hand the shared dialog an account to edit — the alert's
  // own condition comes from the server.
  const billingQuery = useBillingFamilyQuery({ first: 1 })
  if (slot !== 'auto-reload-alert' || !alert) {
    return null
  }
  const account = (billingQuery.data?.me?.ownedAccounts ?? []).find((a) => a.id === alert.accountId) ?? null
  return <AutoReloadAlert alert={alert} account={account} />
}

/**
 * "Your school manages meal ordering for you", one banner per campus. Shows
 * when the campus takes no parent orders (school ordering only — its
 * `orderMethods` omit PARENT, PROD-1554) or its org's ordering config has
 * PARENT_ORDER off (Plan 47). A null config reads as allow-all, so nothing
 * flashes while loading. The view-only behaviour itself (no deadline pills,
 * cart or steppers) keys off the same campus flag in the week and day views.
 */
function CampusParentOrderingGate({ location }: { location: FamilyOrderLocation }) {
  const orgConfigQuery = useOrgOrderingConfigQuery(location.organizationId)
  if (acceptsParentOrders(location) && isChannelEnabled(orgConfigQuery.data ?? null, 'PARENT_ORDER')) {
    return null
  }
  return <ParentOrderingDisabledBanner schoolName={location.name} />
}

function NoStudentsState() {
  return (
    <EmptyState
      icon={<Users className="h-12 w-12" />}
      title="Add a student first"
      description="Head to My Family to add a child, then come back to place an order."
    />
  )
}

/** "Avery", "Avery and Blake", "Avery, Blake, and Casey". */
function listNames(names: string[]): string {
  if (names.length <= 1) {
    return names[0] ?? 'your student'
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`
  }
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
}

/**
 * Students exist but none is linked to a school campus. The primary case is
 * a campus the server flagged for a parent pick/confirm
 * (`needsCampusConfirmation`: no campus at a live org, or an unconfirmed
 * auto-assignment) — the CTA opens the Finish-setup sheet right here.
 * Anything else (e.g. memberships at non-campus locations) falls to a
 * generic "not linked" state with the support hand-off.
 */
function CampusNeededState({ family, onFinishSetup }: { family: MyFamilyResponse | null; onFinishSetup: () => void }) {
  const needingCampus = useMemo(
    () => listStudentsNeedingRequiredInfo(family).filter((student) => student.needsCampusConfirmation),
    [family],
  )
  if (needingCampus.length === 0) {
    // Nothing here is parent-fixable (no campus offered to confirm — org not
    // launched, no live campuses, or memberships at non-campus locations), so
    // the one CTA is the standardized support hand-off, not My Family.
    return (
      <EmptyState
        icon={<School className="h-12 w-12" />}
        title={NO_SCHOOLS_EMPTY.title}
        description={NO_SCHOOLS_EMPTY.description}
        action={<SupportEmailButton subject="Help linking my student to a school campus" />}
      />
    )
  }
  const single = needingCampus.length === 1 ? needingCampus[0]! : null
  const names = listNames(needingCampus.map((student) => student.displayName))
  return (
    <EmptyState
      icon={<School className="h-12 w-12" />}
      title={single ? "Confirm your student's campus" : "Confirm your students' campuses"}
      description={
        single
          ? `Menus can't show until ${names}'s school campus is set.`
          : `Menus can't show until the school campuses for ${names} are set.`
      }
      action={
        <Button variant="default" onClick={onFinishSetup}>
          Confirm campus
        </Button>
      }
    />
  )
}

function ViewLoading() {
  return (
    <div className="flex flex-col gap-3 pt-6">
      <Skeleton className="h-9 w-40" />
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-20 w-full rounded-xl" />
      ))}
    </div>
  )
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <div className="mt-6 rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
      Couldn&apos;t load your family. {message}
    </div>
  )
}
